import { Component } from '@theme/component';

/**
 * Cycles through USP items one at a time on mobile (<=749px).
 * On larger viewports it removes aria-hidden so all items display in the
 * regular flex row.
 *
 * Extends Component so arrow buttons inside it can use `on:click="/next"` /
 * `on:click="/previous"` to drive the slider manually.
 */
export class UspMobileSlider extends Component {
  #current = 0;
  #interval;
  #items = [];
  #mql = window.matchMedia('(max-width: 749px)');

  connectedCallback() {
    super.connectedCallback();

    this.#items = Array.from(this.querySelectorAll('.usp-item'));
    if (this.#items.length <= 1) return;

    this.#handleMediaChange();
    this.#mql.addEventListener('change', this.#handleMediaChange);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clear();
    this.#mql.removeEventListener('change', this.#handleMediaChange);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  next() {
    if (this.#items.length <= 1) return;
    this.#go(this.#current + 1);
  }

  previous() {
    if (this.#items.length <= 1) return;
    this.#go(this.#current - 1);
  }

  #go(index) {
    const length = this.#items.length;
    this.#current = ((index % length) + length) % length;
    this.#updateAria();

    if (this.#mql.matches) this.#startInterval();
  }

  #handleMediaChange = () => {
    if (this.#mql.matches) {
      this.#startInterval();
      this.#updateAria();
    } else {
      this.#clear();
      this.#current = 0;
      this.#items.forEach((item) => item.removeAttribute('aria-hidden'));
    }
  };

  #handleVisibilityChange = () => {
    if (!this.#mql.matches) return;
    if (document.hidden) {
      this.#clear();
    } else {
      this.#startInterval();
    }
  };

  #startInterval() {
    this.#clear();
    const speed = (parseInt(this.getAttribute('autoplay') ?? '', 10) || 3) * 1000;
    this.#interval = window.setInterval(() => {
      this.#current = (this.#current + 1) % this.#items.length;
      this.#updateAria();
    }, speed);
  }

  #clear() {
    if (this.#interval !== undefined) {
      clearInterval(this.#interval);
      this.#interval = undefined;
    }
  }

  #updateAria() {
    this.#items.forEach((item, index) => {
      item.setAttribute('aria-hidden', index === this.#current ? 'false' : 'true');
    });
  }
}

if (!customElements.get('usp-mobile-slider')) {
  customElements.define('usp-mobile-slider', UspMobileSlider);
}
